import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSupabaseAuth } from '../../../contexts/SupabaseAuthContext';
import { toFreeAgentInvoiceAppUrl } from '../../../freeagent/appUrl';
import {
  ACCOUNT_INVOICE_STATUS_LABELS,
  allowedInvoiceActions,
  displayDate,
} from '../accountInvoice.model';
import { accountInvoicesService, type LoadedAccountInvoice } from '../accountInvoices.service';
import {
  ARRANGEMENT_TYPE_LABELS,
  VAT_TREATMENT_LABELS,
  arrangementInvoiceCoverage,
  formatGbp,
  money,
} from '../arrangements.model';
import { InvoiceActionsMenu } from '../components/InvoiceActionsMenu';
import { SendInvoiceModal } from '../components/SendInvoiceModal';

const STATUS_COLOURS = { draft: 'neutral', sent: 'primary', paid: 'success', void: 'danger' } as const;

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography level="body-xs" textColor="neutral.500" textTransform="uppercase" letterSpacing="0.04em">{label}</Typography>
      <Typography level="body-sm" component="div">{children}</Typography>
    </Stack>
  );
}

/** An invoice raised from an arrangement. Separate from the quote/workspace document page. */
const AccountInvoicePage: React.FC = () => {
  const { documentId = '' } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const [loaded, setLoaded] = useState<LoadedAccountInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [issueDate, setIssueDate] = useState('');
  const [terms, setTerms] = useState('');
  const [lineName, setLineName] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const next = await accountInvoicesService.load(documentId);
      setLoaded(next);
      setIssueDate(next.invoice.issueDate);
      setTerms(String(next.invoice.paymentTermsInDays));
      setLineName(next.invoice.lines[0]?.name || '');
      setNotes(next.invoice.notes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the invoice');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (label: string, action: () => Promise<string | void>) => {
    setBusy(label);
    setMessage(null);
    try {
      const text = await action();
      if (text) setMessage({ tone: 'success', text });
      await load(true);
    } catch (err) {
      setMessage({ tone: 'danger', text: err instanceof Error ? err.message : String((err as any)?.message || err) });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error || !loaded) {
    return (
      <Box sx={{ p: 3 }}>
        <Button component={Link} to="/invoicing" size="sm" variant="plain" color="neutral" sx={{ ml: -1, mb: 2 }}>← Invoicing</Button>
        <Alert color="danger">{error || 'Invoice not found'}</Alert>
      </Box>
    );
  }

  const { invoice, arrangement, account } = loaded;
  const allowed = allowedInvoiceActions(invoice.status);
  const arrangementPath = `/invoicing/${invoice.lifecycleRef}`;
  const coverage = arrangement
    ? arrangementInvoiceCoverage(arrangement, invoice.sourceEvent, null)
    : null;
  const termsNumber = Number(terms);
  const draftDirty = allowed.edit && (
    issueDate !== invoice.issueDate
    || termsNumber !== invoice.paymentTermsInDays
    || lineName !== (invoice.lines[0]?.name || '')
    || notes !== (invoice.notes || '')
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: -1 }}>
          <Button component={Link} to="/invoicing" size="sm" variant="plain" color="neutral">Invoicing</Button>
          <Typography level="body-sm" textColor="neutral.400">›</Typography>
          <Button component={Link} to={arrangementPath} size="sm" variant="plain" color="neutral">
            {invoice.lifecycleRef}{arrangement ? ` · ${arrangement.title}` : ''}
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Stack spacing={0.5}>
            <Typography level="body-sm" textColor="neutral.500" fontWeight="lg">Invoice</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography level="h3">{invoice.invoiceRef}</Typography>
              <Chip size="sm" variant="soft" color={STATUS_COLOURS[invoice.status]}>{ACCOUNT_INVOICE_STATUS_LABELS[invoice.status]}</Chip>
              {coverage && <Chip size="sm" variant="outlined">{coverage}</Chip>}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
            {invoice.status !== 'void' && (
              <Button onClick={() => setSendOpen(true)}>Send to customer</Button>
            )}
            <Button variant="outlined" color="neutral" loading={busy === 'pdf'} onClick={() => run('pdf', () => accountInvoicesService.downloadPdf(loaded))}>
              Download PDF
            </Button>
            {allowed.markSent && (
              <Button
                variant="outlined"
                loading={busy === 'sent'}
                onClick={() => {
                  if (!window.confirm(invoice.freeagentInvoiceUrl
                    ? 'Mark as sent? It is already in FreeAgent, so nothing new is created there.'
                    : 'Mark as sent? If FreeAgent sync is on, it is created in FreeAgent now. If you already raised it in FreeAgent by hand, cancel, or it may be duplicated.')) return;
                  void run('sent', async () => { await accountInvoicesService.setStatus(invoice.id, 'sent'); return 'Marked as sent.'; });
                }}
              >
                Mark as sent
              </Button>
            )}
            {allowed.markPaid && (
              <Button color="success" loading={busy === 'paid'} onClick={() => run('paid', async () => { await accountInvoicesService.setStatus(invoice.id, 'paid'); return 'Marked as paid.'; })}>
                Mark as paid
              </Button>
            )}
            <InvoiceActionsMenu
              hideOpen
              invoice={{ id: invoice.id, status: invoice.status, invoiceRef: invoice.invoiceRef, freeagentInvoiceUrl: invoice.freeagentInvoiceUrl }}
              onChanged={() => void load(true)}
              onDeleted={() => navigate(arrangementPath)}
            />
          </Stack>
        </Stack>

        {message && <Alert color={message.tone}>{message.text}</Alert>}

        <Card variant="outlined">
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <Fact label="Bill to">
                {account?.companyName || 'Unknown account'}
                {account?.contactName && <Typography level="body-xs" display="block">{account.contactName}</Typography>}
                {account?.address && (
                  <Typography level="body-xs" textColor="neutral.500" display="block" sx={{ whiteSpace: 'pre-line' }}>{account.address}</Typography>
                )}
              </Fact>
            </Grid>
            <Grid xs={6} md={3}><Fact label="Invoice date">{displayDate(invoice.issueDate)}</Fact></Grid>
            <Grid xs={6} md={3}>
              <Fact label="Due">
                {displayDate(invoice.dueDate)}
                <Typography level="body-xs" textColor="neutral.500" display="block">{invoice.paymentTermsInDays} day terms</Typography>
              </Fact>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Fact label="Arrangement">
                <Link to={arrangementPath}>{invoice.lifecycleRef}</Link>
                {arrangement && (
                  <Typography level="body-xs" textColor="neutral.500" display="block">
                    {ARRANGEMENT_TYPE_LABELS[arrangement.arrangementType]} · {VAT_TREATMENT_LABELS[invoice.vatTreatment]}
                  </Typography>
                )}
              </Fact>
            </Grid>
          </Grid>
        </Card>

        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 80, textAlign: 'right' }}>Qty</th>
                <th style={{ width: 150, textAlign: 'right' }}>Amount (ex VAT)</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.name}{line.description && <Typography level="body-xs" textColor="neutral.500" display="block">{line.description}</Typography>}</td>
                  <td style={{ textAlign: 'right' }}>{line.qty}</td>
                  <td style={{ textAlign: 'right' }}>{formatGbp(line.net)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack spacing={0.4} sx={{ minWidth: 280 }}>
              {[
                ['Net', formatGbp(invoice.netTotal)],
                [invoice.vatTreatment === 'standard' ? `VAT (${Math.round(invoice.vatPct * 1000) / 10}%)` : 'VAT', invoice.vatTreatment === 'standard' ? formatGbp(invoice.vatAmount) : 'Not charged'],
              ].map(([label, value]) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography level="body-sm">{label}</Typography>
                  <Typography level="body-sm">{value}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between">
                <Typography level="body-sm" fontWeight="lg">Total</Typography>
                <Typography level="body-sm" fontWeight="lg">{formatGbp(invoice.grossTotal)}</Typography>
              </Stack>
              {invoice.cisDeduction != null && invoice.cisDeduction > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography level="body-sm">Less CIS{invoice.cisLabourNet != null ? ` on labour ${formatGbp(invoice.cisLabourNet)}` : ''}</Typography>
                    <Typography level="body-sm">− {formatGbp(invoice.cisDeduction)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography level="body-sm" fontWeight="lg">Payable</Typography>
                    <Typography level="body-sm" fontWeight="lg">{formatGbp(money(invoice.grossTotal - invoice.cisDeduction))}</Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        </Sheet>

        {allowed.edit ? (
          <Card variant="outlined">
            <Stack spacing={1.25}>
              <Typography level="title-md">Draft details</Typography>
              <Typography level="body-xs" textColor="neutral.600">
                Amounts come from the arrangement. To change an amount, delete this draft and raise it again.
              </Typography>
              <Grid container spacing={1.5}>
                <Grid xs={6} sm={3}>
                  <FormControl>
                    <FormLabel>Invoice date</FormLabel>
                    <Input type="date" value={issueDate} onChange={(event) => setIssueDate(event.target.value)} />
                  </FormControl>
                </Grid>
                <Grid xs={6} sm={3}>
                  <FormControl>
                    <FormLabel>Terms</FormLabel>
                    <Input type="number" value={terms} onChange={(event) => setTerms(event.target.value)} endDecorator="days" />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Line description</FormLabel>
                    <Input value={lineName} onChange={(event) => setLineName(event.target.value)} />
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Notes (printed on the invoice)</FormLabel>
                    <Textarea minRows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
                  </FormControl>
                </Grid>
              </Grid>
              <Box>
                <Button
                  disabled={!draftDirty}
                  loading={busy === 'save'}
                  onClick={() => run('save', async () => {
                    await accountInvoicesService.updateDraft(invoice.id, {
                      issueDate,
                      paymentTermsInDays: termsNumber,
                      lineId: invoice.lines[0]?.id,
                      lineName,
                      notes,
                    });
                    return 'Saved.';
                  })}
                >
                  Save changes
                </Button>
              </Box>
            </Stack>
          </Card>
        ) : (
          invoice.notes && (
            <Card variant="soft">
              <Fact label="Notes">
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-line' }}>{invoice.notes}</Typography>
              </Fact>
            </Card>
          )
        )}

        <Card variant="outlined">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
            <Stack spacing={0.25}>
              <Typography level="title-sm">FreeAgent</Typography>
              <Typography level="body-sm" textColor="neutral.600">
                {invoice.freeagentInvoiceUrl ? 'In FreeAgent.' : 'Not in FreeAgent yet. Marking it as sent transmits it when sync is on, or send it now.'}
              </Typography>
            </Stack>
            {invoice.freeagentInvoiceUrl ? (
              <Button component="a" href={toFreeAgentInvoiceAppUrl(invoice.freeagentInvoiceUrl)} target="_blank" rel="noopener" variant="outlined">
                Open in FreeAgent
              </Button>
            ) : invoice.status !== 'void' && (
              <Button
                variant="outlined"
                loading={busy === 'freeagent'}
                onClick={() => run('freeagent', async () => {
                  const result = await accountInvoicesService.sendToFreeAgent(invoice.id, user?.id);
                  if (!result.success) throw new Error(result.error || 'FreeAgent did not accept the invoice.');
                  return 'Sent to FreeAgent.';
                })}
              >
                Send to FreeAgent
              </Button>
            )}
          </Stack>
        </Card>
      </Stack>
      <SendInvoiceModal
        invoiceId={invoice.id}
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSent={(text) => {
          setSendOpen(false);
          setMessage({ tone: 'success', text });
          void load(true);
        }}
      />
    </Box>
  );
};

export default AccountInvoicePage;
